import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter } from 'rxjs';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TranslationService } from '@/layout/service/translation.service';
import { MetricChartComponent } from '@/shared/metric-chart/metric-chart.component';
import { MetricDonutComponent } from '@/shared/metric-chart/metric-donut.component';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { CloudFeedbackService } from '../cloud-feedback.service';
import { InsightContext, InsightRange, InsightSummaryStore, insightRangeLabel } from '../insight-panel';
import { FeedbackEntry, FeedbackSummary, averageSeries, feedbackBucketMinutes, feedbackKpis, ratingSegments, responseSeries, starGlyphs } from '@/cloud/models/feedback.models';
import { issueRoute } from '@/cloud/models/github.models';
import { CreateIssueDialogComponent } from '@/cloud/components/create-issue-dialog/create-issue-dialog.component';

/** Which entries the comment list shows. */
export type FeedbackListFilter = 'comments' | 'low' | 'all';

const PAGE_SIZE = 10;

/** How long to wait after a live feedback event before refreshing, so a check-in batch causes one reload. */
export const FEEDBACK_REFRESH_DEBOUNCE_MS = 300;

/** Feedback is shown for every room: the empty state explains how a program enables it. */
export function roomHasFeedback(_context: InsightContext): boolean {
    return true;
}

/**
 * How users rate a room over the selected range: average and response KPIs, a donut of the star
 * distribution, responses and average rating per interval, and the latest entries with their comments.
 * Registered as the "feedback" insight panel; publishes the average rating and response count to the
 * summary strip and refreshes when the room receives new feedback over the WebSocket.
 */
@Component({
    selector: 'app-feedback-insight-panel',
    standalone: true,
    imports: [DatePipe, FormsModule, RouterLink, SelectButtonModule, ButtonModule, MessageModule, MetricChartComponent, MetricDonutComponent, MetricTileComponent, LoadingSkeletonComponent, EmptyStateComponent, CreateIssueDialogComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (ratingsOff()) {
            <p-message class="ratings-off" severity="info" icon="pi pi-eye-slash">{{ t()('feedbackSettings.offNotice') }}</p-message>
        }
        @if (loading() && !summary()) {
            <app-loading-skeleton preset="tiles" [count]="4" [label]="t()('feedback.panelTitle')" />
        } @else if (error()) {
            <app-empty-state icon="fa fa-circle-exclamation" tone="error" [compact]="true" [title]="t()('feedback.loadFailed')" [actionLabel]="t()('insights.retry')" actionIcon="pi pi-refresh" (action)="retry()" />
        } @else if (!kpis().count) {
            <app-empty-state icon="fa fa-star" [compact]="true" [title]="t()('feedback.noneInRange')" [description]="t()('feedback.noneInRangeDetail')" />
        } @else {
            <div class="kpi-row">
                <app-metric-tile class="kpi-average" [label]="t()('feedback.average')" [value]="kpis().average" [unit]="t()('feedback.outOfFive')" icon="fa fa-star" format="1.0-1" [caption]="averageCaption()" />
                <app-metric-tile class="kpi-responses" [label]="t()('feedback.responses')" [value]="kpis().count" icon="fa fa-comments" format="1.0-0" [caption]="rangeLabel()" />
                <app-metric-tile class="kpi-positive" [label]="t()('feedback.positive')" [value]="kpis().positivePct" unit="%" icon="fa fa-face-smile" format="1.0-0" [caption]="t()('feedback.positiveCaption')" />
                <app-metric-tile class="kpi-negative" [label]="t()('feedback.negative')" [value]="kpis().negativePct" unit="%" icon="fa fa-face-frown" format="1.0-0" [caption]="t()('feedback.negativeCaption')" />
            </div>
            <div class="charts">
                <div class="chart chart--distribution">
                    <h3 class="chart-title">{{ t()('feedback.distributionChart') }}</h3>
                    <app-metric-donut
                        [segments]="distribution()"
                        [unit]="t()('feedback.responsesUnit')"
                        format="1.0-0"
                        [centerValue]="kpis().average"
                        centerUnit=""
                        centerFormat="1.0-1"
                        [centerLabel]="t()('feedback.donutCenter')"
                        [ariaLabel]="distributionAria()"
                        [emptyText]="t()('feedback.noneInRange')"
                    />
                </div>
                <div class="chart chart--responses">
                    <h3 class="chart-title">{{ t()('feedback.responsesChart') }}</h3>
                    <app-metric-chart
                        type="bar"
                        [series]="responsesChart()"
                        [unit]="t()('feedback.responsesUnit')"
                        [bucketMinutes]="bucketMinutes()"
                        [windowHours]="range().hours"
                        [ariaLabel]="t()('feedback.responsesChart')"
                        [emptyText]="t()('feedback.noneInRange')"
                    />
                </div>
                <div class="chart chart--average">
                    <h3 class="chart-title">{{ t()('feedback.averageChart') }}</h3>
                    <app-metric-chart
                        type="line"
                        [series]="averageChart()"
                        [unit]="t()('feedback.starsUnit')"
                        [yMax]="5"
                        [bucketMinutes]="bucketMinutes()"
                        [windowHours]="range().hours"
                        height="clamp(10rem, 18vw, 14rem)"
                        [ariaLabel]="t()('feedback.averageChart')"
                        [emptyText]="t()('feedback.noneInRange')"
                    />
                </div>
            </div>
            <section class="entries" aria-labelledby="feedback-entries-title">
                <div class="entries__header">
                    <h3 class="chart-title" id="feedback-entries-title">{{ t()('feedback.recent') }}</h3>
                    <p-selectbutton [options]="filterOptions()" [ngModel]="listFilter()" (ngModelChange)="listFilter.set($event)" optionLabel="label" optionValue="value" [allowEmpty]="false" size="small" [ariaLabel]="t()('feedback.filterLabel')" />
                </div>
                @if (entries().length === 0 && !entriesLoading()) {
                    <p class="entries__empty">{{ t()('feedback.noEntries') }}</p>
                }
                <ul class="entries__list" role="list">
                    @for (entry of entries(); track entry.id) {
                        <li class="entry" [class.entry--low]="entry.rating <= 2" [class.entry--high]="entry.rating >= 4">
                            <div class="entry__stars" [attr.aria-label]="ratingLabel(entry.rating)" [title]="ratingLabel(entry.rating)">{{ stars(entry.rating) }}</div>
                            <div class="entry__body">
                                @if (entry.comment) {
                                    <p class="entry__comment">{{ entry.comment }}</p>
                                } @else {
                                    <p class="entry__comment entry__comment--none">{{ t()('feedback.noComment') }}</p>
                                }
                                <div class="entry__meta">
                                    <span>{{ entry.timestampUtc | date: 'medium' }}</span>
                                    <span>·</span>
                                    <span>{{ sourceLabel(entry) }}</span>
                                    @if (entry.linkedIssue; as linked) {
                                        <a class="entry__issue" [routerLink]="issueLink(linked.repository, linked.number)" [title]="linked.repository"
                                            ><i class="pi pi-github" aria-hidden="true"></i> {{ t()('issues.linkedIssueBadge').replace('{number}', '' + linked.number) }}</a
                                        >
                                    } @else if (canManageIssues()) {
                                        <button type="button" class="entry__raise" (click)="raiseIssue(entry)"><i class="pi pi-github" aria-hidden="true"></i> {{ t()('issues.raiseFromFeedback') }}</button>
                                    }
                                </div>
                            </div>
                        </li>
                    }
                </ul>
                @if (nextCursor()) {
                    <p-button [label]="t()('feedback.loadMore')" icon="pi pi-angle-down" [text]="true" size="small" (onClick)="loadMore()" [loading]="entriesLoading()" />
                }
            </section>
        }
        @if (canManageIssues()) {
            <app-create-issue-dialog [(visible)]="raiseVisible" [locationId]="locationId()" [locationName]="roomName()" [namespaceId]="context().namespaceId" [feedback]="raiseTarget()" [initialTitle]="raiseTitle()" (created)="onIssueCreated()" />
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-4);
                min-width: 0;
                container: feedback / inline-size;
            }
            .kpi-row {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
                gap: var(--app-space-3);
            }
            .charts {
                display: grid;
                grid-template-columns: minmax(0, 1fr);
                gap: var(--app-space-4);
            }
            @container feedback (min-width: 52rem) {
                .charts {
                    grid-template-columns: minmax(16rem, 1fr) minmax(0, 2fr);
                }
                .chart--average {
                    grid-column: 1 / -1;
                }
            }
            .chart {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-2);
                min-width: 0;
            }
            .chart--distribution app-metric-donut {
                flex: 1;
            }
            .chart-title {
                margin: 0;
                font-size: 0.875rem;
                font-weight: 600;
                color: var(--text-color-secondary);
            }
            .entries {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-3);
            }
            .entries__header {
                display: flex;
                flex-wrap: wrap;
                gap: var(--app-space-2) var(--app-space-3);
                align-items: center;
                justify-content: space-between;
            }
            .entries__empty {
                margin: 0;
                font-size: 0.875rem;
                color: var(--text-color-secondary);
            }
            .entries__list {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: var(--app-space-2);
            }
            .entry {
                display: grid;
                grid-template-columns: auto minmax(0, 1fr);
                gap: var(--app-space-3);
                padding: var(--app-space-3);
                border-radius: var(--app-card-radius, 0.75rem);
                background: var(--surface-ground, var(--p-content-hover-background));
                border-inline-start: 3px solid var(--p-surface-400);
            }
            .entry--high {
                border-inline-start-color: var(--p-green-500);
            }
            .entry--low {
                border-inline-start-color: var(--p-red-500);
            }
            .entry__stars {
                font-size: 1rem;
                letter-spacing: 0.1em;
                color: var(--p-yellow-500, #eab308);
                white-space: nowrap;
            }
            .entry__body {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-1);
                min-width: 0;
            }
            .entry__comment {
                margin: 0;
                white-space: pre-wrap;
                overflow-wrap: anywhere;
            }
            .entry__comment--none {
                color: var(--text-color-secondary);
                font-style: italic;
            }
            .entry__meta {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: var(--app-space-2);
                font-size: 0.75rem;
                color: var(--text-color-secondary);
            }
            .entry__issue,
            .entry__raise {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                margin-inline-start: auto;
                padding: 0.15rem 0.6rem;
                border-radius: 999px;
                font: inherit;
                font-weight: 600;
                text-decoration: none;
                cursor: pointer;
            }
            .entry__issue {
                background: color-mix(in srgb, var(--primary-color) 14%, transparent);
                color: var(--primary-color);
            }
            .entry__raise {
                border: 1px solid var(--surface-border);
                background: transparent;
                color: var(--text-color-secondary);
            }
            .entry__raise:hover,
            .entry__issue:hover {
                text-decoration: underline;
            }
        `
    ]
})
export class FeedbackInsightPanelComponent {
    private feedback = inject(CloudFeedbackService);
    private cloudApi = inject(CloudApiService, { optional: true });
    private destroyRef = inject(DestroyRef);
    private summaryStore = inject(InsightSummaryStore, { optional: true });
    readonly t = inject(TranslationService).t;

    readonly context = input.required<InsightContext>();
    readonly range = input.required<InsightRange>();

    readonly summary = signal<FeedbackSummary | null>(null);
    readonly loading = signal(true);
    readonly error = signal(false);

    readonly entries = signal<FeedbackEntry[]>([]);
    readonly nextCursor = signal<string | null>(null);
    readonly entriesLoading = signal(false);
    readonly listFilter = signal<FeedbackListFilter>('comments');
    /** Ratings are turned off for this room (tenant default or its own setting); the history stays visible. */
    readonly ratingsOff = signal(false);

    /** Only the room identity: the context also changes on every live usage update, which must not refetch. */
    readonly locationId = computed(() => this.context().id);
    readonly roomName = computed(() => this.context().location?.name ?? '');
    /** The room's own ratings setting, as its own signal so live usage updates of the context do not refetch. */
    private readonly roomFeedbackOverride = computed(() => this.context().location?.feedbackEnabled ?? null);
    /** The user may raise GitHub issues for this room (issues.manage). */
    readonly canManageIssues = computed(() => this.cloudApi?.hasCapability?.(this.context().namespaceId, 'issues.manage') ?? false);
    readonly raiseVisible = signal(false);
    readonly raiseTarget = signal<FeedbackEntry | null>(null);
    readonly raiseTitle = computed(() => {
        const entry = this.raiseTarget();
        if (!entry) return '';
        const room = this.roomName() || entry.roomName || '';
        return `${room ? room + ': ' : ''}${this.ratingLabel(entry.rating)} ${this.t()('feedback.panelTitle').toLowerCase()}`;
    });
    private request = 0;
    private listRequest = 0;

    readonly kpis = computed(() => feedbackKpis(this.summary()));
    readonly bucketMinutes = computed(() => feedbackBucketMinutes(this.range().hours));
    readonly rangeLabel = computed(() => insightRangeLabel(this.range().hours, this.t()));
    readonly averageCaption = computed(() => {
        const share = this.kpis().commentPct;
        return share == null ? null : this.t()('feedback.commentShare').replace('{pct}', `${Math.round(share)} %`);
    });
    readonly distribution = computed(() => ratingSegments(this.summary(), (star) => this.ratingLabel(star)));
    readonly distributionAria = computed(() => {
        const total = this.distribution().reduce((sum, s) => sum + s.value, 0);
        const parts = this.distribution().map((s) => `${s.label} ${total ? Math.round((s.value / total) * 100) : 0} %`);
        return `${this.t()('feedback.distributionChart')}: ${parts.join(', ')}`;
    });
    readonly responsesChart = computed(() => responseSeries(this.summary(), this.t()('feedback.responses')));
    readonly averageChart = computed(() => averageSeries(this.summary(), this.t()('feedback.average')));
    readonly filterOptions = computed(() => [
        { label: this.t()('feedback.filter.comments'), value: 'comments' as FeedbackListFilter },
        { label: this.t()('feedback.filter.low'), value: 'low' as FeedbackListFilter },
        { label: this.t()('feedback.filter.all'), value: 'all' as FeedbackListFilter }
    ]);

    constructor() {
        effect(() => {
            const locationId = this.locationId();
            const range = this.range();
            untracked(() => void this.load(locationId, range));
        });
        effect(() => {
            const locationId = this.locationId();
            // Re-read when the room's own setting changes (locations:updated patches the loaded location).
            this.roomFeedbackOverride();
            untracked(() => void this.loadAvailability(locationId));
        });
        effect(() => {
            const locationId = this.locationId();
            const range = this.range();
            const filterValue = this.listFilter();
            untracked(() => void this.loadEntries(locationId, range, filterValue, null));
        });

        // Average rating and response count in the page's summary strip.
        effect(() => {
            const { average, count } = this.kpis();
            const t = this.t();
            const caption = this.rangeLabel();
            untracked(() =>
                this.summaryStore?.set(
                    'feedback',
                    average == null
                        ? []
                        : [
                              { key: 'rating', label: t('insights.rating'), value: average, unit: t('feedback.outOfFive'), icon: 'fa fa-star', format: '1.0-1', caption },
                              { key: 'responses', label: t('feedback.responses'), value: count, icon: 'fa fa-comments', format: '1.0-0', caption }
                          ]
                )
            );
        });
        this.destroyRef.onDestroy(() => this.summaryStore?.clear('feedback'));

        // New feedback for this room arrives over the WebSocket; refresh (coalescing bursts from a check-in batch).
        this.cloudApi?.feedbackEvents$
            .pipe(
                filter((event) => event.locationId === this.locationId()),
                debounceTime(FEEDBACK_REFRESH_DEBOUNCE_MS),
                takeUntilDestroyed()
            )
            .subscribe(() => this.retry());

        // Another admin changed the tenant default this room may inherit.
        this.cloudApi?.feedbackSettingsChanged$
            ?.pipe(
                filter((namespaceId) => namespaceId === this.context().namespaceId),
                takeUntilDestroyed()
            )
            .subscribe(() => void this.loadAvailability(this.locationId()));
    }

    stars(rating: number): string {
        return starGlyphs(rating);
    }

    ratingLabel(rating: number): string {
        return this.t()(rating === 1 ? 'feedback.star' : 'feedback.stars').replace('{n}', String(rating));
    }

    sourceLabel(entry: FeedbackEntry): string {
        const kind = this.t()(entry.source === 'panel' ? 'feedback.source.panel' : 'feedback.source.app');
        return entry.sourceName ? `${kind} · ${entry.sourceName}` : kind;
    }

    retry(): void {
        void this.load(this.locationId(), this.range());
        void this.loadEntries(this.locationId(), this.range(), this.listFilter(), null);
    }

    issueLink(repository: string, number: number): string[] {
        return issueRoute(repository, number);
    }

    /** Opens the "Report an issue" dialog pre-filled with this entry; the API quotes and links it. */
    raiseIssue(entry: FeedbackEntry): void {
        this.raiseTarget.set(entry);
        this.raiseVisible.set(true);
    }

    onIssueCreated(): void {
        // The entry now carries linkedIssue; reload the list so the badge replaces the button.
        void this.loadEntries(this.locationId(), this.range(), this.listFilter(), null);
    }

    loadMore(): void {
        const cursor = this.nextCursor();
        if (cursor) void this.loadEntries(this.locationId(), this.range(), this.listFilter(), cursor);
    }

    private async loadAvailability(locationId: string): Promise<void> {
        // Only rooms have a ratings setting.
        if (this.context().location?.type !== 'room') {
            this.ratingsOff.set(false);
            return;
        }
        try {
            const settings = await this.cloudApi?.getLocationFeedbackSettings?.(locationId);
            if (locationId === this.locationId()) this.ratingsOff.set(settings?.effective === false);
        } catch {
            // Only a hint: without it the panel simply shows no notice.
        }
    }

    private async load(locationId: string, range: InsightRange): Promise<void> {
        const request = ++this.request;
        this.loading.set(true);
        this.error.set(false);
        try {
            const summary = await this.feedback.getSummary('locations', locationId, range.from, range.to, feedbackBucketMinutes(range.hours));
            if (request === this.request) this.summary.set(summary);
        } catch {
            if (request !== this.request) return;
            this.summary.set(null);
            this.error.set(true);
        } finally {
            if (request === this.request) this.loading.set(false);
        }
    }

    private async loadEntries(locationId: string, range: InsightRange, filterValue: FeedbackListFilter, cursor: string | null): Promise<void> {
        const request = ++this.listRequest;
        this.entriesLoading.set(true);
        try {
            const page = await this.feedback.list('locations', locationId, {
                from: range.from,
                to: range.to,
                commentsOnly: filterValue === 'comments',
                maxRating: filterValue === 'low' ? 2 : undefined,
                limit: PAGE_SIZE,
                cursor
            });
            if (request !== this.listRequest) return;
            this.entries.set(cursor ? [...this.entries(), ...page.items] : page.items);
            this.nextCursor.set(page.nextCursor);
        } catch {
            if (request !== this.listRequest) return;
            if (!cursor) this.entries.set([]);
            this.nextCursor.set(null);
        } finally {
            if (request === this.listRequest) this.entriesLoading.set(false);
        }
    }
}
